import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { Button, Input, Select, CheckboxGroup } from '@/components/common';
import { authApi } from '@/lib/api';
import type { RegisterRequest, UserRole, DayOfWeek, ApiError } from '@/types';
import { CARETAKER_COLORS, CARETAKER_EMOJIS } from '@/types';

interface CreateAccountFormProps {
  onSwitchToLogin: () => void;
}

const roleOptions = [
  { value: 'parent', label: 'Guardian' },
  { value: 'caretaker', label: 'Caretaker' },
  { value: 'family', label: 'Family' },
  { value: 'professional', label: 'Professional' },
];

const dayOptions = [
  { value: 'monday', label: 'Mon' },
  { value: 'tuesday', label: 'Tue' },
  { value: 'wednesday', label: 'Wed' },
  { value: 'thursday', label: 'Thu' },
  { value: 'friday', label: 'Fri' },
  { value: 'saturday', label: 'Sat' },
  { value: 'sunday', label: 'Sun' },
];

interface FormData extends Omit<RegisterRequest, 'preferred_days_off' | 'unavailable_days'> {
  preferred_days_off: string[];
  unavailable_days: string[];
  color?: string;
  emoji?: string;
}

interface AvailableOptions {
  colors: { available: string[]; all: string[] };
  emojis: { available: string[]; all: string[] };
}

export function CreateAccountForm({ onSwitchToLogin }: CreateAccountFormProps) {
  const { register, isLoading } = useAuth();
  const [formData, setFormData] = useState<FormData>({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    role: 'parent',
    other_role_label: '',
    hourly_wage: undefined,
    max_hours_per_month: undefined,
    preferred_days_off: [],
    unavailable_days: [],
    color: undefined,
    emoji: undefined,
  });
  const [error, setError] = useState<string | null>(null);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [availableOptions, setAvailableOptions] = useState<AvailableOptions | null>(null);
  const [loadingOptions, setLoadingOptions] = useState(false);

  // Fetch available colors/emojis when caretaker role is selected
  useEffect(() => {
    if (formData.role === 'caretaker' && !availableOptions) {
      setLoadingOptions(true);
      authApi.getAvailableOptions()
        .then(setAvailableOptions)
        .catch(console.error)
        .finally(() => setLoadingOptions(false));
    }
  }, [formData.role, availableOptions]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'number' ? (value ? parseFloat(value) : undefined) : value,
    }));
    setError(null);
  };

  const handleDaysChange = (field: 'preferred_days_off' | 'unavailable_days') => (value: string[]) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Basic validation
    if (!formData.email || !formData.password || !formData.first_name) {
      setError('Please fill in all required fields');
      return;
    }

    if (formData.password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (formData.role === 'professional' && !formData.other_role_label) {
      setError('Please specify your occupation');
      return;
    }

    if (formData.role === 'caretaker' && (!formData.color || !formData.emoji)) {
      setError('Please select a color and emoji for your profile');
      return;
    }

    try {
      const registrationData: RegisterRequest = {
        email: formData.email,
        password: formData.password,
        first_name: formData.first_name,
        last_name: formData.last_name || undefined,
        role: formData.role as UserRole,
        other_role_label: formData.role === 'professional' ? formData.other_role_label : undefined,
        hourly_wage: formData.role === 'caretaker' ? formData.hourly_wage : undefined,
        max_hours_per_month: formData.role === 'caretaker' ? formData.max_hours_per_month : undefined,
        preferred_days_off: formData.role === 'caretaker' ? formData.preferred_days_off as DayOfWeek[] : undefined,
        unavailable_days: formData.role === 'caretaker' ? formData.unavailable_days as DayOfWeek[] : undefined,
        color: formData.role === 'caretaker' ? formData.color : undefined,
        emoji: formData.role === 'caretaker' ? formData.emoji : undefined,
      };

      await register(registrationData);
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.message || 'Registration failed. Please try again.');
    }
  };

  const isCaretaker = formData.role === 'caretaker';
  const isProfessional = formData.role === 'professional';

  return (
    <div className="w-full max-w-md">
      <div className="text-center mb-8">
        <div className="flex items-center justify-center gap-2 mb-4">
          <svg className="w-10 h-10 text-accent" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
          </svg>
          <span className="text-2xl font-bold text-white">KaiWatch</span>
        </div>
        <h1 className="text-2xl font-semibold text-white mb-2">Create an account</h1>
        <p className="text-gray-400">Join to help care for Kai</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        {error && (
          <div data-testid="container-register-error" className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
            {error}
          </div>
        )}

        {/* Email */}
        <Input
          label="Email"
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          placeholder="you@example.com"
          autoComplete="email"
          required
          data-testid="input-register-email"
        />

        {/* Password */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Password"
            type="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            placeholder="Min. 8 characters"
            autoComplete="new-password"
            required
            data-testid="input-register-password"
          />
          <Input
            label="Confirm Password"
            type="password"
            name="confirmPassword"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Confirm password"
            autoComplete="new-password"
            required
            data-testid="input-register-confirm-password"
          />
        </div>

        {/* Name */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="First Name"
            type="text"
            name="first_name"
            value={formData.first_name}
            onChange={handleChange}
            placeholder="Your first name"
            autoComplete="given-name"
            required
            data-testid="input-register-first-name"
          />
          <Input
            label="Last Name (optional)"
            type="text"
            name="last_name"
            value={formData.last_name || ''}
            onChange={handleChange}
            placeholder="Your last name"
            autoComplete="family-name"
            data-testid="input-register-last-name"
          />
        </div>

        {/* Role */}
        <Select
          label="Your Role"
          name="role"
          value={formData.role}
          onChange={handleChange}
          options={roleOptions}
          required
          data-testid="select-register-role"
        />

        {/* Conditional fields for "Professional" role */}
        {isProfessional && (
          <Input
            label="Occupation"
            type="text"
            name="other_role_label"
            value={formData.other_role_label || ''}
            onChange={handleChange}
            placeholder="e.g., Nanny, Tutor, Therapist"
            required
            data-testid="input-register-occupation"
          />
        )}

        {/* Conditional fields for Caretaker role */}
        {isCaretaker && (
          <>
            {/* Color Picker */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Choose Your Color <span className="text-red-400">*</span>
              </label>
              {loadingOptions ? (
                <div className="text-gray-400 text-sm">Loading options...</div>
              ) : (
                <div className="flex flex-wrap gap-3">
                  {CARETAKER_COLORS.map((color) => {
                    const isAvailable = availableOptions?.colors.available.includes(color) ?? true;
                    const isSelected = formData.color === color;
                    return (
                      <button
                        key={color}
                        type="button"
                        onClick={() => isAvailable && setFormData(prev => ({ ...prev, color }))}
                        disabled={!isAvailable}
                        data-testid={`clickable-register-color-${color.replace('#', '')}`}
                        className={`w-10 h-10 rounded-full border-2 transition-all ${
                          isSelected
                            ? 'border-white scale-110 ring-2 ring-white/50'
                            : isAvailable
                            ? 'border-transparent hover:scale-105 hover:border-white/50'
                            : 'opacity-30 cursor-not-allowed'
                        }`}
                        style={{ backgroundColor: color }}
                        title={isAvailable ? 'Select this color' : 'Already taken'}
                      />
                    );
                  })}
                </div>
              )}
            </div>

            {/* Emoji Picker */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Choose Your Emoji <span className="text-red-400">*</span>
              </label>
              {loadingOptions ? (
                <div className="text-gray-400 text-sm">Loading options...</div>
              ) : (
                <div className="flex flex-wrap gap-3">
                  {CARETAKER_EMOJIS.map((emoji) => {
                    const isAvailable = availableOptions?.emojis.available.includes(emoji) ?? true;
                    const isSelected = formData.emoji === emoji;
                    return (
                      <button
                        key={emoji}
                        type="button"
                        onClick={() => isAvailable && setFormData(prev => ({ ...prev, emoji }))}
                        disabled={!isAvailable}
                        data-testid={`clickable-register-emoji-${emoji}`}
                        className={`w-12 h-12 text-2xl rounded-lg border-2 transition-all flex items-center justify-center ${
                          isSelected
                            ? 'border-accent bg-accent/20 scale-110'
                            : isAvailable
                            ? 'border-gray-600 hover:border-gray-400 hover:bg-gray-700/50'
                            : 'opacity-30 cursor-not-allowed'
                        }`}
                        title={isAvailable ? 'Select this emoji' : 'Already taken'}
                      >
                        {emoji}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Hourly Wage ($)"
                type="number"
                name="hourly_wage"
                value={formData.hourly_wage || ''}
                onChange={handleChange}
                placeholder="e.g., 25.00"
                min="0"
                step="0.01"
                data-testid="input-register-hourly-wage"
              />
              <Input
                label="Max Hours/Month"
                type="number"
                name="max_hours_per_month"
                value={formData.max_hours_per_month || ''}
                onChange={handleChange}
                placeholder="e.g., 40"
                min="0"
                data-testid="input-register-max-hours"
              />
            </div>

            <CheckboxGroup
              label="Preferred Days Off"
              name="register-dayoff"
              options={dayOptions}
              value={formData.preferred_days_off}
              onChange={handleDaysChange('preferred_days_off')}
              columns={7}
            />

            <CheckboxGroup
              label="Days Cannot Work"
              name="register-unavailable"
              options={dayOptions}
              value={formData.unavailable_days}
              onChange={handleDaysChange('unavailable_days')}
              columns={7}
            />
          </>
        )}

        <Button
          type="submit"
          variant="primary"
          size="lg"
          isLoading={isLoading}
          className="w-full"
          data-testid="clickable-register-submit"
        >
          Create Account
        </Button>

        <p className="text-center text-sm text-gray-400">
          Already have an account?{' '}
          <button
            type="button"
            onClick={onSwitchToLogin}
            className="text-accent hover:text-blue-400 font-medium transition-colors"
            data-testid="clickable-switch-to-login"
          >
            Sign in
          </button>
        </p>
      </form>
    </div>
  );
}

export default CreateAccountForm;
